"use client";
import React, { useEffect, useState, useRef } from "react";
import { InMemoryCache, makeVar, useQuery } from "@apollo/client";
import { GET_USER_FEED } from "@/api/apollo-client/queries/Queries";
import PostCard from "./Posts/PostCard";
import PostDisplay from "@/components/Posts/PostDisplay";
import { useAuth } from "@/contexts/authContext";
import { FeedType, FormattedPost, Post } from "@/types";
import { ArrowUp } from "lucide-react";
import { Virtuoso } from "react-virtuoso";
import { usePagination } from "@/hooks/Pagination/usePagination";
import AdComponent from "./AdSense";
import WhoToFollow from "./WhoToFollow";
import MobileSuggestedUsers from "./MobileSuggestedUsers";

const storedDismissed =
  localStorage.getItem("buttonTooltipDismissed") === "true";
export const buttonTooltipDismissedVar = makeVar<boolean>(storedDismissed);

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        buttonTooltipDismissed: {
          read() {
            return buttonTooltipDismissedVar();
          },
        },
      },
    },
  },
});

const HomePage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>(FeedType.GENERAL);
  const [mediaErrors, setMediaErrors] = useState<Record<string, boolean>>({});
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const showProfileCompletion = user && !user.account_type;

  // Memoize the tabs array to prevent unnecessary re-renders
  const tabs = React.useMemo(
    () => [
      { label: FeedType.GENERAL },
      { label: FeedType.FOLLOWING },
      { label: "PATRONAGE" },
    ],
    []
  );

  // Memoize the formatTimestamp function to prevent recreation
  const formatTimestampMemo = React.useCallback(
    (secondsAgo: number): string => {
      if (secondsAgo < 60) return `${secondsAgo} seconds ago`;
      if (secondsAgo < 3600)
        return `${Math.floor(secondsAgo / 60)} minutes ago`;
      if (secondsAgo < 86400)
        return `${Math.floor(secondsAgo / 3600)} hours ago`;
      if (secondsAgo < 172800) return "Yesterday";
      return new Date(Date.now() - secondsAgo * 1000).toLocaleDateString();
    },
    []
  );

  // Memoize the isUserPost function
  const isUserPostMemo = React.useCallback(
    (post: any) => {
      return post.author?.id === user?.id;
    },
    [user?.id]
  );

  const { data, fetchMore, loading, refetch } = useQuery(GET_USER_FEED, {
    variables: {
      input: {
        user_id: user?.id,
        type: activeTab,
        pagination: {
          first: 20,
          after_time: null,
          after_uid: null,
        },
      },
    },
    skip: !user,
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
    nextFetchPolicy: "cache-first",
  });

  // ✅ Optimized pagination hook for Virtuoso's sliding window
  const {
    items: allPosts,
    loadMore: originalLoadMore,
    hasMore,
    loading: loadingMore,
  } = usePagination<Post>({
    fetchMore,
    initialData: data?.getUserFeed || [],
    pageSize: 20,
    getLastCursor: (items) => {
      const last = items[items.length - 1];
      return last ? { timestamp: last.createdAt, id: last.id } : null;
    },
    extractNewItems: (result) => result?.data?.getUserFeed || [],
    onError: (err) => console.error("Pagination error:", err),
    additionalVariables: {
      user_id: user?.id,
      type: activeTab,
    },
    getItemId: (item) => item.id, // Ensure proper ID extraction
  });

  // Reset and refetch data when user or tab changes
  useEffect(() => {
    if (user?.id) {
      refetch();
    }
  }, [user?.id, activeTab, refetch]);

  const handleMediaError = React.useCallback(
    (postId: number, mediaIndex: number) => {
      const errorKey = `${postId}-${mediaIndex}`;
      setMediaErrors((prev) => ({ ...prev, [errorKey]: true }));
    },
    []
  );

  const scrollToTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle scroll events to show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        const scrollTop = scrollRef.current.scrollTop;
        setShowScrollTop(scrollTop > 300);
      }
    };

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll, { passive: true });
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Create a combined list with PostCard as first item
  const combinedItems = React.useMemo(() => {
    return [{ type: "postCard" }, ...allPosts];
  }, [allPosts]);

  // Memoize the itemContent function outside of conditional rendering
  const itemContent = React.useCallback(
    (index: number, item: any) => {
      // Handle PostCard as first item
      if (index === 0 && item.type === "postCard") {
        return <PostCard key="postCard" scrollRef={scrollRef} />;
      }

      // Handle regular posts
      const post = item as Post;
      const formatted: FormattedPost = {
        id: post.id,
        display_name: post.author.display_name,
        username: post.author.name,
        handle: `@${post.author.name.toLowerCase().replace(/\s/g, "")}`,
        type: post.type,
        avatar: post.author.profile_picture?.url || "",
        content: post.content,
        media: post.media,
        likes: post.likes,
        total_reactions: post.total_reactions,
        impressions: post.impressions,
        reaction_by_me: post.reaction_by_me,
        comments: post.comments,
        total_comments: post.total_comments,
        reposts: post.reposts || 0,
        location: post.location_name || post.location?.type || null,
        location_name: post.location_name || null,
        location_coordinates: post.location?.coordinates || undefined,
        links: post.links,
        timestamp: formatTimestampMemo(post.seconds_ago),
        mentioned: post.mentioned?.map((m: any) => ({
          id: m.id,
          name: m.name,
          profile_bio: m.profile_bio,
          avatar: m.profile_picture?.url || "",
        })),
        tags: post.tags?.map((t: any) => ({
          id: t.tag_id,
          name: t.tag_name,
        })),
        associated_with: post.associated_with?.map((m: any) => ({
          id: m.id,
          name: m.name,
          avatar: m.profile_picture?.url || "",
        })),
        isUserPost: isUserPostMemo(post),
        author: post.author,
        seconds_ago: post.seconds_ago,
        ai_analysis: post.ai_analysis,
        automated_post: post.automated_post || false,
        createdAt: post.createdAt,
      };

      // Calculate the actual post index (accounting for PostCard if present)
      const actualPostIndex = item.type === "postCard" ? index - 1 : index;

      return (
        <React.Fragment key={formatted.id}>
          <PostDisplay
            posts={[formatted]}
            context="homepage"
            onMediaError={handleMediaError}
            mediaErrors={mediaErrors}
          />
          {/* Show ad every 6 posts (after posts 6, 12, 18, etc.) */}
          {(actualPostIndex + 1) % 6 === 0 && (
            <AdComponent
              adType="feed"
              id={`${formatted.id}-ad-${actualPostIndex}`}
            />
          )}

          {actualPostIndex === 1 && (
            <div className="lg:hidden md:hidden block  ">
              <MobileSuggestedUsers />
            </div>
          )}
        </React.Fragment>
      );
    },
    [formatTimestampMemo, isUserPostMemo, mediaErrors, handleMediaError]
  );

  // Don't render until we have data or are loading
  if (!user) return null;

  return (
    <div className="flex flex-col flex-1 relative layout-theme backdrop-blur-2xl ">
      {/* <header className="sticky top-0 z-10 backdrop-blur-3xl border-b border-gray-200 dark:border-gray-800 pt-2"> */}
      {/* TODO Remove this commented section when Feed Tabs caching problem is fixed */}
      {/* <div className="flex mt-2">
          {tabs.map((tab) => (
            <button
              key={tab.label}
              className={`flex-1 py-2 font-medium ${
                activeTab === tab.label ? "border-b-4 border-blue-500" : ""
              }`}
              onClick={() => setActiveTab(tab.label)}
            >
              {tab.label.charAt(0).toUpperCase() +
                tab.label.slice(1).toLowerCase()}
            </button>
          ))}
        </div> */}
      {/* </header> */}

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="absolute bottom-5 right-0 -translate-x-1/2 z-50 bg-[var(--primary)] text-primary p-2 rounded-full shadow-md hover:bg-[var(--primary)] transition"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}

      <main className="flex-1 flex overflow-y-auto [scrollbar-width:none] z-0">
        <div className="flex-grow">
          {/* Show loading state while fetching initial data */}
          {loading && allPosts.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            /* Virtuoso handles virtualization + infinite scroll with sliding window */
            <Virtuoso
              key={`${user?.id}-${activeTab}`}
              style={{ height: "100vh" }}
              data={combinedItems}
              endReached={originalLoadMore}
              atBottomThreshold={10} // Trigger loadMore when 10 elements remain
              overscan={6}
              increaseViewportBy={{ top: 800, bottom: 1200 }}
              // Remove totalListHeightChanged to prevent excessive pagination calls
              // endReached will handle pagination properly
              itemContent={itemContent}
              components={{
                Footer: () =>
                  loadingMore ? (
                    <div className="p-4 flex justify-center">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : null,
              }}
              // Performance optimizations for sliding window
              useWindowScroll={false}
              followOutput={false}
              alignToBottom={false}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default HomePage;
